/**
 * ENG-5452 / FE6 — the roll-up's filter bar.
 *
 * Four controls, one per usable api facet, and every one of them narrows the
 * query rather than the page in hand. `workApproverId` is the fifth facet and is
 * deliberately absent: it matches the FROZEN participant row, which disagrees
 * with the live project-owner set the moment ownership moves, so a control
 * labelled "work approver" would hide rows whose displayed work approver is
 * exactly the person filtered for.
 *
 * Status is pills and the rest are selects because status is the one facet with
 * a fixed, short vocabulary that a reviewer toggles constantly.
 */
import type { RollupFacets, RollupFilterState } from './accountRollupViewModel';
import { ROLLUP_CREATED_RANGES, ROLLUP_STATUS_FILTERS } from './accountRollupViewModel';

const SELECT_CLASS =
  'min-h-48 rounded-lg border border-surface-border bg-surface-card px-12 py-8 text-on-surface body-2 disabled:cursor-not-allowed disabled:text-on-surface-subtle';

interface AccountRollupFiltersProps {
  value: RollupFilterState;
  facets: RollupFacets;
  optionsTruncated?: boolean;
  /** The options query failed, so the pickers are empty for a reason worth saying. */
  optionsFailed?: boolean;
  /** The options query has not answered yet, so the pickers are empty for now. */
  optionsPending?: boolean;
  onChange: (next: RollupFilterState) => void;
}

export default function AccountRollupFilters({
  value,
  facets,
  optionsTruncated,
  optionsFailed,
  optionsPending,
  onChange,
}: AccountRollupFiltersProps): JSX.Element {
  return (
    <>
      <div className="flex flex-wrap items-center gap-8" role="tablist" aria-label="Status filter">
        {ROLLUP_STATUS_FILTERS.map((filter) => (
          <button
            key={filter.key}
            type="button"
            role="tab"
            aria-selected={value.statusKey === filter.key}
            onClick={() => onChange({ ...value, statusKey: filter.key })}
            // `min-h-48`, not `min-h-44`: 44 is absent from this config's
            // spacing scale (…40, 48…), so `min-h-44` emits no CSS at all.
            className={`min-h-48 rounded-full border px-12 py-6 transition-colors subtitle-2 ${
              value.statusKey === filter.key
                ? 'border-brand-primary-faint bg-brand-light text-on-surface'
                : 'border-surface-border bg-surface-elevated text-on-surface-muted hover:bg-surface-secondary'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-12 sm:flex-row sm:flex-wrap sm:items-end">
        <label className="flex min-w-0 flex-col gap-4 sm:flex-1">
          <span className="text-on-surface-muted util-caption">Expert</span>
          <select
            className={SELECT_CLASS}
            value={value.expertId}
            disabled={optionsPending}
            onChange={(e) => onChange({ ...value, expertId: e.target.value })}
          >
            <option value="">Any expert</option>
            {facets.experts.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex min-w-0 flex-col gap-4 sm:flex-1">
          <span className="text-on-surface-muted util-caption">Project</span>
          <select
            className={SELECT_CLASS}
            value={value.projectId}
            disabled={optionsPending}
            onChange={(e) => onChange({ ...value, projectId: e.target.value })}
          >
            <option value="">Any project</option>
            {facets.projects.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex min-w-0 flex-col gap-4 sm:flex-1">
          <span className="text-on-surface-muted util-caption">Opened</span>
          <select
            className={SELECT_CLASS}
            value={value.rangeKey}
            onChange={(e) => onChange({ ...value, rangeKey: e.target.value })}
          >
            {ROLLUP_CREATED_RANGES.map((range) => (
              <option key={range.key} value={range.key}>
                {range.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Say so whenever the pickers are less than complete. Silence is the worse
          failure: an expert missing from the list reads as "no engagements with
          them", which is a different claim from "not in the most recent 200",
          from "we couldn't fetch them" and from "not fetched yet". */}
      {optionsPending ? (
        <p className="text-on-surface-muted util-caption" role="status">
          Still loading the expert and project options. The status and date filters already work.
        </p>
      ) : null}

      {optionsFailed ? (
        <p className="text-on-surface-muted util-caption" role="status">
          We couldn&rsquo;t load the expert and project options. The status and date filters still
          work.
        </p>
      ) : null}

      {optionsTruncated ? (
        <p className="text-on-surface-muted util-caption">
          Expert and project options come from this account&rsquo;s 200 most recently active
          engagements.
        </p>
      ) : null}
    </>
  );
}
